import React from 'react';
import { AbsenceFilesPlaceholderIcon, AlertIcon, Anchor, Box, Inscription, NoSearchItemsPlaceholder, NoUsersIcon, Preloader, SearchInput } from '@common-fe/common-fe';
import styled from 'styled-components';

import { useCombineHSASection } from './hooks/useCombineHSASection';
import { CombineHSASectionType, HSACombineAccount } from './types/combineHSA.types';
import { CombineHSAEmployeeCard } from './CombineHSAEmployeeCard';

const SEARCH_INPUT_WIDTH = '312px';

const StyledSearchInput = styled(SearchInput)`
  input {
    background-color: ${({ theme }) => theme.colors.accentContainer};
    border: 1px solid ${({ theme }) => theme.colors.border1};
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.textBody};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  input::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    opacity: 1;
  }
`;

const SECTION_CONFIG: Record<CombineHSASectionType, { badgeBackground: string; badgeColor: string; badgeText: string; title: string }> = {
  [CombineHSASectionType.FROM]: {
    title: 'Transfer from:',
    badgeText: 'Will be closed',
    badgeBackground: 'dangerContainer',
    badgeColor: 'danger',
  },
  [CombineHSASectionType.TO]: {
    title: 'Transfer to:',
    badgeText: 'Receiving funds',
    badgeBackground: 'successContainer',
    badgeColor: 'success',
  },
};

interface Props {
  sectionType: CombineHSASectionType;
  currentAccountNumber: string;
  employeeId: string;
  ssn: string;
  firstName?: string;
  lastName?: string;
  selectedFromId: string | null;
  selectedToId: string | null;
  otherSectionAccount?: HSACombineAccount | null;
  hasError: boolean;
  onSelect: (account: HSACombineAccount) => void;
  onClearSelection?: () => void;
}

export const CombineHSASection: React.FC<Props> = ({
  currentAccountNumber,
  employeeId,
  ssn,
  firstName,
  lastName,
  hasError,
  onSelect,
  onClearSelection,
  sectionType,
  selectedFromId,
  selectedToId,
  otherSectionAccount,
}) => {
  const { accounts, isLoading, isManualSearchEnabled, searchValue, handleSearchChange } = useCombineHSASection({
    accountNumber: currentAccountNumber,
    employeeId,
    ssn,
    firstName,
    lastName,
    sectionType,
  });

  const { badgeBackground, badgeColor, badgeText, title } = SECTION_CONFIG[sectionType];

  return (
    <Box data-testid={`combine-hsa-section_${sectionType}_wrapper`} direction="column" gap="spacing16">
      <Box direction="row" align="center" justify="between" gap="spacing16">
        <Box direction="row" align="center" gap="spacing12" wrap>
          <Inscription weight="bold" color="textTitle" lineHeight="24px">
            {title}
          </Inscription>
          <Box
            background={badgeBackground}
            round="999px"
            pad={{ horizontal: 'spacing8', vertical: 'spacing4' }}
          >
            <Inscription
              size="small"
              weight="bold"
              color={badgeColor}
              lineHeight="18px"
            >
              {badgeText}
            </Inscription>
          </Box>
        </Box>
        <Inscription color="textSecondary" lineHeight="20px" weight="normal">
          Suggested
        </Inscription>
      </Box>
      {hasError && (
        <Box data-testid={`combine-hsa-section_${sectionType}_error`} direction="row" align="center" gap="spacing4">
          <AlertIcon size="20px" />
          <Inscription color="danger" lineHeight="20px">Select an account to proceed.</Inscription>
        </Box>
      )}
      <Box data-testid={`combine-hsa-section_${sectionType}_search`} direction="row" align="center" gap="spacing18">
        <Box style={{ width: SEARCH_INPUT_WIDTH, maxWidth: '100%' }}>
          <StyledSearchInput
            placeholder="Search for full name, SSN, EID, PID"
            value={searchValue}
            onChange={handleSearchChange}
          />
        </Box>
        {sectionType === CombineHSASectionType.FROM && onClearSelection && (
          <Anchor
            data-testid="combine-hsa-section_clear-button"
            disabled={!selectedFromId && !selectedToId}
            onClick={onClearSelection}
            label="Clear Selection"
            size="medium"
            weight="normal"
          />
        )}
      </Box>
      {isLoading ? <Preloader /> : (
        accounts.length ? (
          <Box data-testid={`combine-hsa-section_${sectionType}_accounts-list`} direction="column" gap="spacing8">
            {accounts.map((account) => (
              <CombineHSAEmployeeCard
                key={`${sectionType}-${account.accountNumber}`}
                account={account}
                sectionType={sectionType}
                isSelected={sectionType === CombineHSASectionType.FROM ? account.accountId === selectedFromId : account.accountId === selectedToId}
                isDisabledByOtherSection={sectionType === CombineHSASectionType.FROM ? account.accountId === selectedToId : account.accountId === selectedFromId}
                otherSectionAccount={otherSectionAccount}
                onSelect={onSelect}
              />
            ))}
          </Box>
        ) : (
          <NoSearchItemsPlaceholder
            data-testid={`combine-hsa-section_${sectionType}_empty-state`}
            placeholderTitle={isManualSearchEnabled ? 'There are no results' : 'There are no suggested accounts'}
            node={
              isManualSearchEnabled
                ? <NoUsersIcon size="xxlarge" color="iconSecondary" />
                : <AbsenceFilesPlaceholderIcon size="xxlarge" color="iconSecondary" />
            }
          />
        )
      )}
    </Box>
  );
};
